import { z } from 'zod';
import { DECISIONS } from '../lmd/engine';
import { PageQuerySchema } from './common';

export const DOCUMENT_TYPES = [
  'PV',
  'RELEVE',
  'ATTESTATION_INSCRIPTION',
  'RECU',
  'CONVENTION_STAGE',
] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];
export const DOCUMENT_STATUSES = ['pending', 'ready', 'failed', 'revoked'] as const;

/** Academic period of a document; formatted by the client in the UI language. */
export const PeriodSchema = z.object({
  yearLabel: z.string(),
  semester: z.number().int().nullable(),
  levelCode: z.string().nullable(),
});
export type Period = z.infer<typeof PeriodSchema>;

export const DocumentSchema = z.object({
  id: z.uuid(),
  type: z.enum(DOCUMENT_TYPES),
  status: z.enum(DOCUMENT_STATUSES),
  fileName: z.string(),
  verificationCode: z.string(),
  studentId: z.uuid().nullable(),
  studentName: z.string().nullable(),
  matricule: z.string().nullable(),
  period: PeriodSchema.nullable(),
  issuedByName: z.string().nullable(),
  createdAt: z.string(),
  issuedAt: z.string().nullable(),
  sentAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  revokeReason: z.string().nullable(),
});
export type IssuedDocument = z.infer<typeof DocumentSchema>;

export const DocumentListQuerySchema = PageQuerySchema.extend({
  type: z.enum(DOCUMENT_TYPES).optional(),
  status: z.enum(DOCUMENT_STATUSES).optional(),
  studentId: z.uuid().optional(),
  q: z.string().trim().max(100).optional(),
});
export type DocumentListQuery = z.infer<typeof DocumentListQuerySchema>;

export const DocumentPageSchema = z.object({
  items: z.array(DocumentSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});

export const TranscriptOptionsSchema = z.object({
  enrollmentId: z.uuid(),
  semester: z.coerce.number().int().min(1).max(12),
  showCourses: z.union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')]).default(true),
  qr: z.union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')]).default(true),
});
export type TranscriptOptions = z.infer<typeof TranscriptOptionsSchema>;

export const CertificateOptionsSchema = z.object({ enrollmentId: z.uuid() });

export const RevokeDocumentSchema = z.object({
  reason: z.string().trim().min(5, 'documents.errors.reason').max(500),
});

/** Transcript semesters the registrar can issue (frozen by a jury). */
export const IssuableSemesterSchema = z.object({
  enrollmentId: z.uuid(),
  index: z.number().int(),
  yearLabel: z.string(),
  levelCode: z.string(),
  /** Valid transcripts already issued for this semester (one per option set). */
  issued: z.array(
    z.object({
      id: z.uuid(),
      verificationCode: z.string(),
      status: z.enum(DOCUMENT_STATUSES),
      showCourses: z.boolean(),
      qr: z.boolean(),
    }),
  ),
});
export type IssuableSemester = z.infer<typeof IssuableSemesterSchema>;

// ── Immutable payloads (stored on the document at issue time) ─────────────────
const PersonSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  gender: z.enum(['F', 'M']).nullable(),
  matricule: z.string(),
  cin: z.string().nullable(),
  birthDate: z.string().nullable(),
  birthPlace: z.string().nullable(),
});
const ProgramSchema = z.object({ name: z.string(), cycle: z.enum(['ingenieur', 'licence', 'master']) });
const SignatorySchema = z.object({
  name: z.string(),
  title: z.string().nullable(),
  city: z.string().nullable(),
});

export const TranscriptPayloadSchema = z.object({
  kind: z.literal('RELEVE'),
  student: PersonSchema,
  program: ProgramSchema,
  levelCode: z.string(),
  groupName: z.string().nullable(),
  yearLabel: z.string(),
  semester: z.number().int(),
  session: z.enum(['principale', 'rattrapage']),
  units: z.array(
    z.object({
      code: z.string(),
      name: z.string(),
      credits: z.number(),
      average: z.number().nullable(),
      result: z.enum(['acq', 'comp', 'nacq']).nullable(),
      courses: z.array(
        z.object({
          name: z.string(),
          coefficient: z.number(),
          credits: z.number(),
          average: z.number().nullable(),
        }),
      ),
    }),
  ),
  summary: z.object({
    average: z.number().nullable(),
    creditsAcquired: z.number(),
    creditsTotal: z.number(),
    mention: z.string().nullable(),
    decision: z.enum(DECISIONS).nullable(),
  }),
  options: z.object({ showCourses: z.boolean(), qr: z.boolean() }),
  signatory: SignatorySchema,
});
export type TranscriptPayload = z.infer<typeof TranscriptPayloadSchema>;

export const CertificatePayloadSchema = z.object({
  kind: z.literal('ATTESTATION_INSCRIPTION'),
  student: PersonSchema,
  program: ProgramSchema,
  levelCode: z.string(),
  levelLabel: z.string(),
  yearLabel: z.string(),
  enrolledOn: z.string().nullable(),
  signatory: SignatorySchema,
});
export type CertificatePayload = z.infer<typeof CertificatePayloadSchema>;

/** Convention de stage: school, host company, student(s), subject, dates and supervisors at approval. */
export const ConventionPayloadSchema = z.object({
  kind: z.literal('CONVENTION_STAGE'),
  internshipCode: z.string(),
  typeName: z.string(),
  isPfe: z.boolean(),
  title: z.string(),
  description: z.string().nullable(),
  startsOn: z.string(),
  endsOn: z.string(),
  yearLabel: z.string(),
  students: z.array(
    PersonSchema.extend({ levelLabel: z.string(), program: ProgramSchema, email: z.string().nullable() }),
  ),
  company: z.object({
    name: z.string(),
    city: z.string().nullable(),
    address: z.string().nullable(),
    supervisorName: z.string().nullable(),
    supervisorEmail: z.string().nullable(),
    supervisorPhone: z.string().nullable(),
  }),
  academicSupervisor: z.string().nullable(),
  signatory: SignatorySchema,
});
export type ConventionPayload = z.infer<typeof ConventionPayloadSchema>;

/** Reçu de paiement: printed once the money is cleared (cash, transfer, cleared cheque). */
export const ReceiptPayloadSchema = z.object({
  kind: z.literal('RECU'),
  receiptNumber: z.string(),
  student: z.object({ firstName: z.string(), lastName: z.string(), matricule: z.string() }),
  program: ProgramSchema.nullable(),
  levelCode: z.string().nullable(),
  yearLabel: z.string(),
  installment: z.object({ label: z.string(), note: z.string().nullable(), dueOn: z.string() }),
  amountMillimes: z.number().int(),
  method: z.enum(['cash', 'cheque', 'transfer', 'online']),
  chequeNumber: z.string().nullable(),
  bank: z.string().nullable(),
  reference: z.string().nullable(),
  receivedAt: z.string(),
  receivedBy: z.string().nullable(),
  /** Balance of the year after this payment. */
  remainingMillimes: z.number().int(),
});
export type ReceiptPayload = z.infer<typeof ReceiptPayloadSchema>;

/** Public verification page: what can be shown to anyone holding the code. */
export const VerifyResultSchema = z.object({
  status: z.enum(['valid', 'revoked']),
  type: z.enum(DOCUMENT_TYPES),
  holder: z.string().nullable(),
  matricule: z.string().nullable(),
  program: z.string().nullable(),
  period: PeriodSchema.nullable(),
  issuedAt: z.string().nullable(),
  signatory: z.string().nullable(),
  revokedAt: z.string().nullable(),
  /** Receipts: amount and number printed on the paper, to detect altered copies. */
  amountMillimes: z.number().int().nullable().default(null),
  reference: z.string().nullable().default(null),
});
export type VerifyResult = z.infer<typeof VerifyResultSchema>;
